import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import * as api from '../api';

const CompanyContext = createContext(null);

export const DEFAULT_COMPANY = {
  businessName: 'My Store',
  contactAddress: '',
  phoneNumber: '',
  email: '',
  website: '',
  logo: '',
  logoBase64: null,
  paymentDetails: '',
  paymentQr: '',
  vatOptions: 'common',
  vatType: 'exclusive',
  commonVatRate: 0,
  showStoke: false,
  showStock: false,
  enableHoldKot: false,
  holdKotEntitled: true,
  enableShiftTracking: false,
  shiftTrackingEntitled: true,
  goodsReturnEntitled: true,
  customInvoiceFormatEntitled: false,
  useCustomInvoiceFormat: false,
  invoiceSignatoryName: '',
  invoiceSignatoryTitle: '',
  invoicePleaseNoteText: '',
  defaultInvoiceView: 'THERMAL',
  enableA4InvoiceView: true,
  currency: 'THB',
  currencySettingsEntitled: true,
  allowNegativeStock: false,
  invoicePrintCount: 'SINGLE',
  activityLogsVisible: true,
  // Special Features (see server/models/GlobalSettingsModel.js) - default
  // OFF, the inverse of the *Entitled fields above. Consumers must gate on
  // `=== true`, not the `!== false` convention used for those.
  lockItemPriceOverrides: false,
  minSaleQuantityEnabled: false,
  backDateInvoiceEntitled: false,
  backDateInvoiceMaxDays: 7,
  reportVisibility: {},
  // SuperAdmin-only, global (see server/models/GlobalSettingsModel.js) -
  // mirrors the schema default so POS checkout rounds correctly even before
  // the first /company response lands.
  roundingRules: { THB: 0.5, INR: 0.5 },
};

export function CompanyProvider({ children }) {
  const [company, setCompany] = useState(null);
  const [loading, setLoading] = useState(false);
  // Tracks whether a company has ever loaded, without fetchCompany closing
  // over `company` state itself - a ref instead of the `company` value in
  // the useCallback dependency array keeps fetchCompany's identity stable
  // across renders. It previously depended on [company], so every
  // successful fetch (setCompany(data), a fresh object every time) gave it
  // a new identity - which re-fired every effect that depended on
  // fetchCompany (see App.js), which called fetchCompany again, which set
  // company again... an infinite refetch loop, visible as the repeated
  // /company network calls.
  const hasLoadedRef = useRef(false);

  const fetchCompany = useCallback(async () => {
    // This provider is mounted above the auth-aware part of the tree (see
    // providers.js - it wraps /login, /forgot, /reset/:token too), so its
    // own focus-listener/30s interval below have no other way to know
    // there's nobody logged in. Without this, every public page fired a
    // doomed /company request on a timer forever - a 401 in the console,
    // and "auth middleware: ... Authentication token required" spamming
    // the server logs, purely from someone sitting on the login screen.
    // PermissionsContext.js already guards its own poll the same way.
    let hasToken = false;
    try {
      hasToken = !!JSON.parse(localStorage.getItem("profile") || "null")?.token;
    } catch (err) {
      hasToken = false;
    }
    if (!hasToken) return null;

    // Only show the loading state on the very first fetch, so a background
    // refresh (window focus/the 30s interval below) doesn't flicker the UI.
    setLoading(!hasLoadedRef.current);
    try {
      const { data } = await api.fetchCompany();
      if (data) {
        setCompany(data);
        hasLoadedRef.current = true;
      }
      return data;
    } catch (err) {
      console.warn('Failed to fetch company settings:', err?.response?.data?.message || err?.message);
      return null;
    } finally {
      setLoading(false);
    }
  }, []);

  // Keep company settings updated in real time when window gains focus or on interval
  React.useEffect(() => {
    const handleFocus = () => {
      fetchCompany();
    };
    window.addEventListener('focus', handleFocus);
    // 30-second interval to propagate SuperAdmin global changes across staff screens
    const interval = setInterval(fetchCompany, 30000);
    return () => {
      window.removeEventListener('focus', handleFocus);
      clearInterval(interval);
    };
  }, [fetchCompany]);

  const saveCompany = useCallback(async (updates) => {
    setLoading(true);
    try {
      const { data } = await api.updateCompany(updates);
      if (data) {
        setCompany(data);
      }
      return data;
    } catch (err) {
      console.error('Failed to save company settings:', err);
      throw err;
    } finally {
      setLoading(false);
    }
  }, []);

  const value = {
    company: company ? { ...DEFAULT_COMPANY, ...company } : DEFAULT_COMPANY,
    rawCompany: company,
    loading,
    fetchCompany,
    saveCompany,
    setCompany,
  };

  return (
    <CompanyContext.Provider value={value}>
      {children}
    </CompanyContext.Provider>
  );
}

export function useCompany() {
  const context = useContext(CompanyContext);
  if (!context) {
    // Fallback if rendered outside CompanyProvider: safe defaults with no-op functions
    return {
      company: DEFAULT_COMPANY,
      rawCompany: null,
      loading: false,
      fetchCompany: async () => DEFAULT_COMPANY,
      saveCompany: async () => DEFAULT_COMPANY,
      setCompany: () => {},
    };
  }
  return context;
}

export default CompanyContext;
